import {
  Bone,
  Color,
  Group,
  Mesh,
  Object3D,
  Scene,
  Material,
  Vector3,
  SkinnedMesh,
  Skeleton,
  Euler,
} from "three";
import { GLTF, GLTFLoader, SkeletonUtils } from "three-stdlib";

import { Context } from "../../context";

import {
  Player,
  InferredBatPosition,
  SkeletalPose,
  teamIdToAbbrev,
} from "../../../lib/types/statsApi";
import { disposeMeshGeometry, fetchExtractJson } from "../../../lib/misc/util";
import {
  Armature,
  ConstraintMetadataEntry,
  PointMetadata,
} from "../../../lib/ik";
import {
  ArmatureSkeleton,
  PointSkeleton,
  SkeletonHelperGlobal,
} from "../../../lib/misc/helpers";
import {
  EdgeStructureTreeBuilder,
  LeadingEdgeTreeBuilder,
} from "../../../lib/ik/builders";
import { FabrikChain, KabschGroup } from "../../../lib/ik/group";
import { BoneStats } from "../../../lib/misc/measure";
import { scaleModelConnected, scaleModelRBI } from "../../util/models";
import { extractPosition, Frame, Frames } from "../../util/frame";
import { Nameplate } from "./nameplate";
import { EdgeStructure } from "../../../lib/ik/metadata";
import { Edge } from "../../../lib/ik/edge";
import {
  Outfit,
  OutfitHandedness,
  OutfitMetadata,
} from "../../../lib/skin/outfit";
import {
  MaterialTextures,
  MaterialTexturesMetadata,
} from "../../../lib/skin/material";
import { PositionDetails } from "../../../lib/misc/position";
import { JointPositions } from "../../../lib/types";
import { groundLevel } from "../../../lib/misc/ground";
import { calculateAlpha, PoserClock } from "../../util/clock";
import { ActorDisc } from "./disc";

export interface ActorDebugObjects {
  pointSkel?: PointSkeleton;
  armatureSkel?: ArmatureSkeleton;
  groundSpot?: Mesh;
}

export const positionAbbr = new Map([
  [1, "P"],
  [2, "C"],
  [3, "1B"],
  [4, "2B"],
  [5, "3B"],
  [6, "SS"],
  [7, "LF"],
  [8, "CF"],
  [9, "RF"],
  [10, "B"],
  [11, "R1"],
  [12, "R2"],
  [13, "R3"],
]);

// TODO: move hard coded strings out
export const pseudoRootName = "Neck";

export enum ActorType {
  CONNECTED = "CONNECTED",
  RBI = "RBI",
}

export type ActorTransition = "in" | "out" | "none";

const batLeftGrip = new Vector3();
const batRightGrip = new Vector3();
const fingerBend = new Euler(-Math.PI / 3, 0, 0);
const thumbBend = new Euler(-Math.PI / 4, 0, 0);

interface ArmGroup {
  leftHand?: Object3D;
  rightHand?: Object3D;
  leftElbow?: Object3D;
  rightElbow?: Object3D;
}

// TODO: Make this extend SceneObjectBase
/**
 * Wrapper class that contains the three.js model and the IK Armature object
 */
export class Actor {
  static maxAge = 200;
  static maxTransitionDeferal = 30 * 10;
  static maxInterpolationDistSq = 6 ** 2; // ft^2

  static actorDefaultTransition: ActorTransition = "out";

  id: number;

  isPartOfScene = false;

  private prevFrameNum = -1;

  private _age = 0;

  private armature?: Armature;

  private type: ActorType;

  private boneStats?: BoneStats;

  private outfit?: Outfit;
  private material?: MaterialTextures;

  private model?: Object3D;
  private skeleton?: SkeletonHelperGlobal;
  private lerpGroundY = new Vector3();

  nameplate?: Nameplate;

  disc: ActorDisc;

  private posedJoints = new JointPositions();
  private posedDetails = new PositionDetails(["_waist", "ROOT"]);

  _nameplateVisible = false;

  bones: Group = new Group();
  armGroup: ArmGroup = {};

  private contextRef: Context;

  private player?: Player;
  private nextPlayer?: Player;
  // TODO: set this to true whenever we seek
  private playerTransitionPending = false;
  private playerTransitionCounter = 0;

  private outfitTemplate?: OutfitMetadata;
  private materialTemplate?: MaterialTexturesMetadata;

  private lastPos?: Vector3;
  private discontThreshold = 5;

  private transitionAlpha: number;
  private transitionLimitsY = [-10, 0];
  private deltaTransition = 0.01;
  private currentTransition?: ActorTransition;

  centroid: Object3D = new Object3D();

  constructor(context: Context, id: number, player?: Player) {
    this.contextRef = context;

    this.id = id;

    this.type = context.modelType;

    this.currentTransition = Actor.actorDefaultTransition;

    this.transitionAlpha = this.currentTransition == "none" ? 1 : 0;

    this.disc = new ActorDisc(context);

    this._setPlayer(player);

    this.setModelVisibility(false);
  }

  private set nameplateVisible(visible: boolean) {
    this._nameplateVisible = visible;
    if (this.nameplate) this.nameplate.object.visible = visible;
  }

  addToScene(scene: Scene) {
    if (!this.model || !this.skeleton) {
      console.error(`no model present for actor ${this.id}`);
      return;
    }

    scene.add(this.model);
    scene.add(this.skeleton);
    scene.add(this.bones);
    if (this.nameplate) scene.add(this.nameplate.object);
    scene.add(this.disc.object);

    this.isPartOfScene = true;
  }

  removeFromScene(scene: Scene) {
    if (this.model) scene.remove(this.model);
    if (this.skeleton) scene.remove(this.skeleton);
    scene.remove(this.bones);
    if (this.nameplate) scene.remove(this.nameplate.object);
    scene.remove(this.disc.object);

    this.isPartOfScene = false;

    this.playerTransitionPending = true;
  }

  setTransition(direction: ActorTransition) {
    this.currentTransition = direction;
  }

  setModelVisibility(visible: boolean) {
    this.bones.visible = visible;
    if (this.model) this.model.visible = visible;
  }

  setSkeletonVisibility(visible: boolean) {
    if (this.skeleton) this.skeleton.visible = visible;
  }

  setPlayer(player: Player): boolean {
    if (this.isOrWillBePlayer(player)) {
      return false;
    }

    this._age = 0;

    this.nextPlayer = player;
    return true;
  }

  isOrWillBePlayer(player: Player): boolean {
    return (
      player.person.id == this.player?.person.id ||
      player.person.id == this.nextPlayer?.person.id
    );
  }

  /**
   * Age actor, resolve pending player change and interpolate poses
   * @param frames
   */
  update(frames: Frames, clock: PoserClock, debug?: ActorDebugObjects) {
    const jointMap = this.contextRef.jointMap;

    const time = clock.realMs;

    this.updatePlayer();

    if (!this.isPartOfScene) {
      return;
    }

    if (frames.current && this.prevFrameNum != frames.current.num) {
      for (const frame of [...frames.missed, frames.current]) {
        if (frame) {
          const position = extractPosition(frame, this.id);
          if (position) {
            const points = jointMap.asJointPositions(position);
            debug?.pointSkel?.update(points);

            if (position.posed) {
              this._age = 0;

              if (!this.lastPos) {
                this.lastPos = new Vector3();
              } else {
                const pos = position.posed.centroid;
                if (
                  this.lastPos.distanceToSquared(pos) > this.discontThreshold
                ) {
                  this.playerTransitionPending = true;
                }
              }

              this.lastPos.copy(position.posed.centroid);
            }
          } else {
            this._age++;
          }
        }
      }

      this.prevFrameNum = frames.current.num;
    }

    const deltaAlpha = 30 * clock.delta * this.deltaTransition;

    if (this.currentTransition) {
      switch (this.currentTransition) {
        case "in":
          this.transitionAlpha = Math.min(this.transitionAlpha + deltaAlpha, 1);
          if (this.transitionAlpha >= 1) {
            this.currentTransition = undefined;
          }
          break;

        case "out":
          this.transitionAlpha = Math.max(this.transitionAlpha - deltaAlpha, 0);
          if (this.transitionAlpha <= 0) {
            this.currentTransition = undefined;
          }
          break;

        case "none":
          this.transitionAlpha = 1;
          break;

        default:
          break;
      }
    }

    if (this.interpolate(frames, time, debug)) {
      this.setModelVisibility(true);
    }

    if (this.id == 10) this.stickHandsToBat();
  }

  private interpolate(
    frames: Frames,
    time: number,
    debug?: ActorDebugObjects
  ): boolean {
    if (!this.armature) {
      console.error("no armature");
      return false;
    }

    const neighbors = frames.getNeighbours<SkeletalPose>(
      (frame) => extractPosition(frame, this.id)?.posed
    );
    if (!neighbors || (!neighbors.prev && !neighbors.next)) {
      // actor should be aged at the very least in the update function in this case
      this._age = Actor.maxAge;
      return false;
    }

    // NB: fallback to next = prev
    if (!neighbors.next) {
      neighbors.next = neighbors.prev;
    }

    if (
      !neighbors ||
      neighbors.prev?.time == undefined ||
      neighbors.next?.time == undefined
    ) {
      // actor should be aged at the very least in the update function in this case
      this._age = Actor.maxAge;
      console.warn(`unexpected failure to interpolate for actor ${this.id}`);
      return false;
    }

    // don't interpolate over large distances
    const delta = neighbors.next.neighbor.centroid.distanceToSquared(
      neighbors.prev.neighbor.centroid
    );
    if (delta > Actor.maxInterpolationDistSq) {
      return false;
    }

    const interpAlpha = calculateAlpha(
      [neighbors.prev.time, neighbors.next.time],
      time
    );
    if (interpAlpha == undefined) {
      return false;
    }

    this.armature.interpolate(
      neighbors.prev.neighbor.state,
      neighbors.next.neighbor.state,
      interpAlpha
    );

    this.posedJoints.clear();
    this.armature.root.getPoints(this.posedJoints);
    this.posedDetails.update(this.posedJoints);

    const transAlpha = Math.min(1, Math.max(this.transitionAlpha, 0));
    const transitionY =
      this.transitionLimitsY[0] * (1 - transAlpha) +
      this.transitionLimitsY[1] * transAlpha;

    const ground = this.posedDetails.ground;

    const stadiumGroundLevel = groundLevel(ground.x, ground.z);
    this.lerpGroundY.setY(-ground.y + stadiumGroundLevel + transitionY);

    this.armature.moveBy(this.lerpGroundY);

    if (debug) {
      debug.groundSpot?.position.copy(ground);
      debug.armatureSkel?.update();
      debug.pointSkel?.setDelta(this.lerpGroundY);
    }

    if (this.nameplate?.object.visible) {
      this.nameplate.update(ground, transAlpha);
    }

    this.disc.update(ground, transAlpha, stadiumGroundLevel);

    this.centroid.position.lerpVectors(
      neighbors.prev.neighbor.centroid,
      neighbors.next.neighbor.centroid,
      interpAlpha
    );

    return true;
  }

  tooOld(): boolean {
    return this._age > Actor.maxAge;
  }

  stickHandsToBat() {
    let bat = this.contextRef.world?.bat;
    const { leftHand, rightHand, leftElbow, rightElbow } = this.armGroup;

    if (!bat || !leftHand || !leftElbow || !rightHand || !rightElbow) return;

    if (this.player?.person.batSide.code == "L") {
      batLeftGrip.copy(bat.topHand.position);
      batRightGrip.copy(bat.bottomHand.position);
    } else if (this.player?.person.batSide.code == "R") {
      batLeftGrip.copy(bat.bottomHand.position);
      batRightGrip.copy(bat.topHand.position);
    } else {
      return;
    }

    // check if hand is close enough to bat to attach then move hand towards
    // elbow to visually move the hand itself onto the bat instead of the wrist
    let dist = leftHand.position.distanceToSquared(batLeftGrip);
    if (dist < 0.0625)
      leftHand.position.lerpVectors(batLeftGrip, leftElbow.position, 0.25);

    dist = rightHand.position.distanceToSquared(batRightGrip);
    if (dist < 0.0625)
      rightHand.position.lerpVectors(batRightGrip, rightElbow.position, 0.25);
  }

  private updatePlayer() {
    // fallback timer to ensure we don't leave people incorrectly outfitted for too long
    if (
      this.nextPlayer &&
      this.playerTransitionCounter++ > Actor.maxTransitionDeferal
    ) {
      this.playerTransitionPending = true;
    }
    if (this.playerTransitionPending && this.nextPlayer) {
      this._setPlayer(this.nextPlayer);
      this.nextPlayer = undefined;
      this.playerTransitionCounter = 0;
      this.playerTransitionPending = false;
    }
  }

  private _setPlayer(player?: Player) {
    if (
      !this.contextRef.playerFont ||
      (player && player.person.id == this.player?.person.id)
    )
      return;

    const { frames, jointMap, world } = this.contextRef;

    this.player = player;

    const boneStats = new BoneStats();
    boneStats.processPlay(frames, this.id, extractPosition, jointMap);

    this.boneStats = boneStats;

    this.makeModel();

    const processMaterials = (m: Material) => {
      // if (!m.userData?.csmSetup && world?.lights.csm) {
      //   world?.lights.csm.setupMaterial(m);
      //   m.userData.csmSetup = true;
      // }
    };

    this.outfit = new Outfit(undefined, undefined, processMaterials);
    this.material = new MaterialTextures(this.contextRef);

    const { team, outfitTemplate, materialTemplate } = playerDetails(
      this.contextRef,
      player,
      this.id
    );

    if (!team) {
      console.warn(`setting player ${this.id} with no team`);
      if (this.model) this.model.visible = false;
      if (this.nameplate) this.nameplate.object.visible = false;
    } else {
      this.outfitTemplate = outfitTemplate;
      this.materialTemplate = materialTemplate;

      this.applySkin(player);
    }

    this.nameplateVisible = this.contextRef.state.nameplatePositions.includes(
      this.id
    );

    if (this.nameplate) {
      this.nameplate.object.traverse(disposeMeshGeometry);
      this.contextRef.scene.remove(this.nameplate.object);
    }

    if (player) {
      const teamColor = new Color(
        this.material.getColors()?.primary
      ).convertSRGBToLinear();

      const nameForPlate =
        typeof player.person.boxscoreName === "string"
          ? player.person.boxscoreName.split(", ")[0]
          : player.person.lastName;

      const transAlpha = Math.min(1, Math.max(this.transitionAlpha, 0));

      this.nameplate = new Nameplate(
        nameForPlate,
        this.id,
        player.person.batSide.code,
        transAlpha,
        this.contextRef.playerFont,
        this.contextRef.playerFontAlt,
        teamColor
      );

      this.nameplate.object.visible = this._nameplateVisible;
      this.contextRef.scene.add(this.nameplate.object);
    }
  }

  refreshDebugSkeletons(context: Context) {
    if (!this.armature) {
      console.error(`no armature for actor ${this.id}`);
      return;
    }
    if (context.debugObjects.armatureSkel) {
      context.scene.remove(context.debugObjects.armatureSkel);
    }

    const armatureSkel = new ArmatureSkeleton(this.armature.root);
    armatureSkel.visible = context.state.fabrikSkel;

    context.scene.add(armatureSkel);
    context.debugObjects.armatureSkel = armatureSkel;
  }

  teardown(removeNameplate = true) {
    if (this.isPartOfScene) {
      this.removeFromScene(this.contextRef.scene);
    }

    this.model?.traverse(disposeMeshGeometry);
    this.skeleton?.traverse(disposeMeshGeometry);
    this.bones.traverse(disposeMeshGeometry);
    if (removeNameplate) {
      this.nameplate?.teardown();
    }

    this.model = undefined;
    this.skeleton = undefined;
    this.nameplate = undefined;
    this.bones.clear();
  }

  private makeModel() {
    const { constrData, edgeStructure, modelTemplate, pointData, state } =
      this.contextRef;

    if (!modelTemplate) {
      console.error("modelTemplate not set");
      return;
    }

    if (!this.boneStats) {
      console.error("no bone stats calculated");
      return;
    }

    if (this.model) {
      this.teardown(false);
    }
    this.model = SkeletonUtils.clone(modelTemplate);

    // share skeleton across model
    let sharedSkeleton: Skeleton | undefined = undefined;
    this.model.traverse((obj) => {
      if (obj instanceof SkinnedMesh) {
        if (!sharedSkeleton) {
          sharedSkeleton = obj.skeleton;
        } else {
          obj.bind(sharedSkeleton, obj.matrixWorld);
        }
      }
    });

    this.skeleton = new SkeletonHelperGlobal(this.model);

    this.skeleton.frustumCulled = false;

    this.model.frustumCulled = false;
    this.model.visible = state.model;
    this.skeleton.visible = false;

    if (this.type == ActorType.CONNECTED) {
      if (!scaleModelConnected(this.boneStats, this.model, this.id)) {
        throw new Error("unable to scale model");
      }
    } else if (this.type == ActorType.RBI) {
      if (!edgeStructure) {
        throw new Error("no edge structure loaded");
      }
      if (!scaleModelRBI(this.boneStats, this.model, edgeStructure, this.id)) {
        throw new Error("unable to scale model");
      }
    }

    const armature = this.buildArmature(edgeStructure, constrData, pointData);

    this.armature = armature;

    if (this.id == 10) this.closeFists();
  }

  // TODO: in iterating the metadata, this got very messy - clean up when we
  //       rethink the metadata
  private buildArmature(
    edgeStructure?: EdgeStructure,
    constraints?: ConstraintMetadataEntry[],
    pointData?: PointMetadata
  ): Armature | undefined {
    if (!this.model) {
      console.error(`no model for actor ${this.id}`);
      return undefined;
    }

    const rootName =
      this.type == ActorType.CONNECTED
        ? "_waist"
        : this.type == ActorType.RBI
        ? "joint_Char"
        : "something-broke";

    const root = this.model.getObjectByName(rootName);
    if (!root) {
      throw new Error("no root found");
    }

    const leafBones: Bone[] = [];
    let rootEdge: Edge | undefined;

    if (this.type == ActorType.CONNECTED) {
      rootEdge = LeadingEdgeTreeBuilder(root as Bone);
    } else if (this.type == ActorType.RBI && edgeStructure) {
      rootEdge = EdgeStructureTreeBuilder(
        root as Bone,
        edgeStructure,
        leafBones
      );
    } else {
      throw new Error("incorrect actor type");
    }

    if (!rootEdge) {
      throw new Error("unable to create edge tree");
    }

    const rootJoint = rootEdge.start;
    const kabschNames = ["Neck", "RHip", "LHip"];
    const kabschChildren = [];

    for (const kName of kabschNames) {
      const joint = rootJoint.getChildJoint(kName);
      if (joint) {
        kabschChildren.push(joint);
      } else {
        console.error(`missing joint ${kName} in structure`);
      }
    }

    const rootKabsch = new KabschGroup(rootEdge.end, kabschChildren);
    const armature = new Armature(rootEdge, constraints, false, rootKabsch);
    if (pointData) {
      armature.parseGroups(pointData, pseudoRootName);
    }

    for (const group of armature.groups) {
      if (group instanceof FabrikChain && group.effector.name == "Neck") {
        group.annealable = true;
      }
    }

    // remove root fabrik chain - we're posing this with Kabsch
    // TODO: improve this when we redo metadata
    armature.groups.pop();

    // flatten out bones into global space Group
    const boneList: Bone[] = [];
    const toVisit: Object3D[] = [this.model];

    for (
      let current: Object3D | undefined = this.model;
      current;
      current = toVisit.pop()
    ) {
      current.frustumCulled = false;
      if (!leafBones.includes(current as Bone)) {
        toVisit.push(...current.children);
      }

      if (current.type === "Bone") {
        boneList.push(current as Bone);
      }
    }

    for (const bone of boneList) {
      this.bones.attach(bone);
    }

    this.armGroup.leftHand = this.bones.children.find((bone) =>
      bone.name.includes("joint_HandL")
    );
    this.armGroup.rightHand = this.bones.children.find((bone) =>
      bone.name.includes("joint_HandR")
    );
    this.armGroup.leftElbow = this.bones.children.find((bone) =>
      bone.name.includes("joint_ElbowL")
    );
    this.armGroup.rightElbow = this.bones.children.find((bone) =>
      bone.name.includes("joint_ElbowR")
    );

    return armature;
  }

  private closeFists() {
    let { leftHand, rightHand } = this.armGroup;
    if (!leftHand || !rightHand) return;

    let bend = (fingerBone: Object3D) => {
      if (fingerBone.name.includes("joint_Thumb")) {
        fingerBone.quaternion.setFromEuler(thumbBend);
        return;
      }
      fingerBone.quaternion.setFromEuler(fingerBend);
      fingerBone.children.forEach((child) => bend(child));
    };

    leftHand.children.forEach((finger) => bend(finger));
    rightHand.children.forEach((finger) => bend(finger));
  }

  private applySkin(player?: Player) {
    if (!this.model) {
      console.error(`no model for actor ${this.id}`);
      return;
    }

    if (!this.outfit || !this.material) {
      console.error("no outfit or material defined");
      return;
    }

    if (this.outfitTemplate) {
      this.outfit.fromJSON(this.outfitTemplate);
    } else {
      console.error(`missing outfit template for ${this.id}`);
    }

    const { gameState, state } = this.contextRef;

    let handedness = (
      this.id == 10
        ? gameState.getCurrentPlay()?.matchup.batSide.description
        : this.id == 1
        ? gameState.getCurrentPlay()?.matchup.pitchHand.description
        : player?.person.pitchHand.description
    ) as OutfitHandedness | undefined;
    if (
      !(
        handedness == undefined ||
        handedness == "Left" ||
        handedness == "Right"
      )
    ) {
      console.error("invalid handed code:", handedness);
      handedness = undefined;
    }
    this.outfit.apply(
      [this.model, this.bones],
      false,
      handedness,
      state.lodLevel
    );

    if (this.materialTemplate) {
      this.material.fromJSON(this.materialTemplate);
    } else {
      console.error(`missing material template for ${this.id}`);
    }

    this.material.apply([this.model, this.bones]);
  }
}

export enum PlayerType {
  BATTER = "batter",
  CATCHER = "catcher",
  PITCHER = "pitcher",
  FIELDER = "fielder",
  UMPIRE = "umpire",
  PLATE_UMPIRE = "plate-umpire",
  COACH = "coach",
}

export function getPlayerType(id: number): PlayerType {
  switch (id) {
    case 1:
      return PlayerType.PITCHER;
    case 10:
    case 11:
    case 12:
    case 13:
      return PlayerType.BATTER;
    case 2:
      return PlayerType.CATCHER;
    case 18:
    case 19:
      return PlayerType.COACH;
    case 14:
      return PlayerType.PLATE_UMPIRE;
    case 15:
    case 16:
    case 17:
    case 20:
    case 21:
      return PlayerType.UMPIRE;
    default:
      return PlayerType.FIELDER;
  }
}

function playerDetails(
  context: Context,
  player: Player | undefined,
  id: number
) {
  const battingTeam = context.gameState.getBattingTeam();
  const fieldingTeam = context.gameState.getFieldingTeam();

  const playerType = getPlayerType(id);

  let team: string | undefined = undefined;

  if (
    playerType == PlayerType.UMPIRE ||
    playerType == PlayerType.PLATE_UMPIRE
  ) {
    team = "umpire";
  } else {
    if (player?.parentTeamId) {
      team = teamIdToAbbrev[player.parentTeamId];
    } else {
      // fallback in case no parentTeamId
      switch (playerType) {
        case PlayerType.BATTER:
        case PlayerType.COACH:
          team = battingTeam;
          break;

        case PlayerType.PITCHER:
        case PlayerType.CATCHER:
        case PlayerType.FIELDER:
          team = fieldingTeam;
          break;

        default:
          team = undefined;
          break;
      }
    }
  }

  let outfitTemplate: OutfitMetadata | undefined = undefined;
  let materialTemplate: MaterialTexturesMetadata | undefined = undefined;

  if (team) {
    outfitTemplate = context.outfitTemplates.get(playerType);
    materialTemplate = context.materialsTemplates.get(team);
  }

  return {
    team: team,
    outfitTemplate: outfitTemplate,
    materialTemplate: materialTemplate,
  };
}

const gltfLoader = new GLTFLoader();
export function getGltf(url: string): Promise<GLTF> {
  return gltfLoader.loadAsync(url);
}

export function findActor(context: Context, posId: number): Actor | undefined {
  return context.actors.get(posId);
}

export async function fetchActorResources(context: Context) {
  const assetBase = context.options.assetBase;
  const modelUrl = `${assetBase}/models/${context.state.modelName}`;
  return getGltf(modelUrl)
    .then((gltf) => (context.modelTemplate = gltf.scene))
    .then(() => fetch(`${assetBase}/constraints/test-rbi.json`))
    .then(fetchExtractJson)
    .then((data) => {
      context.constrData = data;
    })
    .then(() => {
      // load outfit metadata
      const outfitNames = Object.values(PlayerType);
      const promises: Promise<any>[] = outfitNames.map((name) => {
        return fetch(`${assetBase}/skins/outfits/${name}.json`)
          .then(fetchExtractJson)
          .then((data) => {
            context.outfitTemplates.set(name, data);
          })
          .catch((error) => console.error(error));
      });
      // TODO: improve this - where do we define what outfits we have?
      return Promise.all(promises);
    })
    .then(() => {
      const materials: [string, string][] = [["umpire", ""]];
      // load outfit metadata
      context.teamAndPlayerInfo
        .getAwayPlayersParentTeamIds()
        ?.forEach((teamId) => {
          if (teamId) {
            // default to LAD away for games without uniform textures
            if (teamIdToAbbrev[teamId] == undefined) {
              teamIdToAbbrev[teamId] = "LAD";
            }
            materials.push([teamIdToAbbrev[teamId], "_AWAY"]);
          } else {
            console.error(`no team id for away team`);
          }
        });
      context.teamAndPlayerInfo
        .getHomePlayersParentTeamIds()
        ?.forEach((teamId) => {
          if (teamId) {
            // default to SF home for games without uniform textures
            if (teamIdToAbbrev[teamId] == undefined) {
              teamIdToAbbrev[teamId] = "SF";
            }
            materials.push([teamIdToAbbrev[teamId], "_HOME"]);
          } else {
            console.error(`no team id for away team`);
          }
        });

      return fetchMaterialsForTeam(context, materials);
    })
    .then(() => {
      // load point metadata
      return fetch(`${context.options.assetBase}/points/connected.json`)
        .then(fetchExtractJson)
        .then((data) => {
          context.pointData = data;
        })
        .catch((error) => console.error(error));
    })
    .then(() => {
      // load point metadata
      return fetch(`${context.options.assetBase}/ik/rbi.json`)
        .then(fetchExtractJson)
        .then((data) => {
          context.edgeStructure = data;
        })
        .catch((error) => console.error(error));
    });
}

function fetchMaterialsForTeam(
  context: Context,
  names: [string, string][]
): Promise<any> {
  const promises: Promise<any>[] = names.map(async (name) => {
    try {
      const response = await fetch(
        `${context.options.assetBase}/skins/materials/${name.reduce(
          (p, v) => p + v,
          ""
        )}.json`
      );

      const data = await fetchExtractJson(response);
      if (name) {
        context.materialsTemplates.set(name[0], data);
      } else {
        console.error(
          "team abbreviation is null, can't grab materials template"
        );
      }
    } catch (error) {
      return console.error(error);
    }
  });
  // TODO: improve this - where do we define what materials we have?
  return Promise.all(promises);
}
